import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { twMerge } from "tailwind-merge";
import { Button, Typography } from "@pondigitalsolutions/pon-component-library";
import { ChevronDown } from "untitledui-js/react";

import {
  useGetSession,
  useGetChatTitle,
  ChatMessage,
  useSendStreamingMessage,
  useGenerateChatTitle,
} from "@hooks/chat";
import { AiMessage } from "@components/search/results/AiMessage";
import { Layout } from "@components/Layout";
import { Alert } from "@components/Alert";
import { StoryblokSearchResults } from "@components/search/StoryblokSearchResults";
import { languageEnglish } from "../i18n";
import { useGetSources } from "@hooks/chat/useGetSources";
import { Sources } from "@components/search/results/Sources";
import { SHORT_MESSAGE_LENGTH_THRESHOLD } from "../constants";

export const SearchResultsPage = () => {
  const { chatId, lang } = useParams<{ chatId: string; lang?: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  // State passed via URL
  const messageFromUrl = searchParams.get("message");

  // Refs
  const parentIdRef = useRef<string | null>(null);
  const autoSentRef = useRef<boolean>(false);
  const currentChatIdRef = useRef<string | null>(null);

  // Component state
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [persistedSearchTerm, setPersistedSearchTerm] = useState<string>("");

  // derived state
  const lastUserMessage = messages.find(
    (message) => message.role === "user"
  )?.content;

  // React query - query hooks
  const {
    data: existingMessages,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useGetSession(chatId || null);
  const {
    data: sourcesData = [],
    isLoading: isLoadingSourcesData,
    isError: isSourcesDataError,
  } = useGetSources(lastUserMessage || null);
  // Get chat title
  const { data: chatTitleData } = useGetChatTitle(chatId || null);

  // React query - mutation hooks
  const { mutate: generateTitle } = useGenerateChatTitle();
  const { mutate: sendStreamingMessage, isPending: isSendingMessage } =
    useSendStreamingMessage();

  // Check if error is a 404 (not found) error
  const isNotFoundError =
    messagesError &&
    typeof messagesError === "object" &&
    "response" in messagesError &&
    (messagesError as { response: { status: number } }).response?.status ===
      404;

  // Handle 404 redirect
  useEffect(() => {
    if (isNotFoundError) {
      const redirectPath = lang === languageEnglish ? "/en/404" : "/404";
      void navigate(redirectPath, { replace: true });
    }
  }, [isNotFoundError, navigate, lang]);

  const sendMessage = useCallback(
    (sessionId: string, message: string) => {
      // Add user message to chat immediately
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: message,
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMessage]);

      // Create placeholder assistant message
      const assistantMessageId = `assistant-${Date.now()}`;
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setIsStreaming(true);
      setStreamError(null);

      // Send message with streaming
      sendStreamingMessage({
        chatId: sessionId,
        message,
        parentId: parentIdRef.current ?? sessionId,
        onContent: (content: string) => {
          // Update the assistant message with streaming content
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, content: msg.content + content }
                : msg
            )
          );
        },
        onComplete: (messageId: string) => {
          // Update the message ID with the real one from the server
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId ? { ...msg, id: messageId } : msg
            )
          );
          setIsStreaming(false);
          parentIdRef.current = messageId;

          // Generate title for the chat session
          generateTitle(sessionId, {
            onSuccess: () => {
              // Invalidate queries to refresh the list with updated titles
              void queryClient.invalidateQueries({
                queryKey: ["getSessions"],
              });
            },
            onError: (error: unknown) => {
              console.error("Failed to generate chat title:", error);
              // Still invalidate sessions in case there were other changes
              void queryClient.invalidateQueries({
                queryKey: ["getSessions"],
              });
            },
          });
        },
        onError: (error: string) => {
          console.error("Stream error:", error);
          setStreamError(error);
          setIsStreaming(false);

          // Update the assistant message to show error
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? {
                    ...msg,
                    content: t("OmniSearch.errorResponse"),
                  }
                : msg
            )
          );
        },
      });
    },
    [sendStreamingMessage, generateTitle, queryClient, t]
  );

  // Load existing chat if chatId is provided
  useEffect(() => {
    // guard - when chatId actually changes
    if (currentChatIdRef.current && currentChatIdRef.current !== chatId) {
      // Reset value of parentRefId
      parentIdRef.current = null;
      // Reset persisted search term
      setPersistedSearchTerm("");
      // Reset messages state
      setMessages([]);
    }

    currentChatIdRef.current = chatId || null;

    // guard - when chatId is provided and there are messages
    if (chatId && existingMessages && existingMessages.length > 0) {
      setMessages(existingMessages);
      // Set parent ID to the last message for continuing the conversation
      const lastMessage = existingMessages[existingMessages.length - 1];
      parentIdRef.current = lastMessage.id;

      // Extract search term from the first user message for Storyblok search
      const firstUserMessage = existingMessages.find(
        (message) => message.role === "user"
      );
      if (firstUserMessage?.content) {
        setPersistedSearchTerm(firstUserMessage.content);
      }
    }
    // Reset auto-sent flag when chatId changes
    autoSentRef.current = false;
  }, [chatId, existingMessages]);

  // Auto-send message from URL if present
  useEffect(() => {
    if (messageFromUrl && chatId && !messages.length && !autoSentRef.current) {
      // Mark as sent to prevent re-execution
      autoSentRef.current = true;

      // Persist the search term for Storyblok search before clearing from URL
      setPersistedSearchTerm(messageFromUrl);

      // Clear the message from URL to avoid resending
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("message");
      void navigate(lang ? `/${lang}/search/${chatId}` : `/search/${chatId}`, {
        replace: true,
      });

      // Send the message
      setTimeout(() => {
        sendMessage(chatId, messageFromUrl);
      }, 100);
    }
  }, [
    messageFromUrl,
    chatId,
    messages.length,
    isLoadingMessages,
    searchParams,
    navigate,
    sendMessage,
    lang,
  ]);

  const handleToggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  // Check if we have any messages to display
  const hasMessages = messages.length > 0;

  // Check if the first response message (not the question) length is less than threshold
  const isMessageShort =
    (messages[1]?.content?.length ?? 0) < SHORT_MESSAGE_LENGTH_THRESHOLD;

  // Determine container height based on message state and expand/collapse state
  let containerHeight = "h-auto";
  if (hasMessages && !isOpen && !isMessageShort) {
    containerHeight = "h-[430px]";
  }

  // Generate dynamic title
  const pageTitle = chatTitleData?.title || t("OmniSearch.chatSession");

  return (
    <Layout translatedSlug="">
      <title>{pageTitle}</title>

      {/* AI Search Results */}
      <div className="flex flex-col bg-secondary-900 dark:bg-secondary-950">
        <div
          className={twMerge(
            "relative container mx-auto mb-8 flex max-w-4xl flex-col gap-2 overflow-y-clip pt-6 xl:px-4",
            containerHeight
          )}
        >
          {/* Error State for Messages (but not 404 errors) */}
          {messagesError && !isNotFoundError && (
            <div className="flex h-full items-center justify-center">
              <Alert
                text="Failed to load chat messages"
                labelText="Error"
                variant="error"
                data-testid="message-error-alert"
                textClassName="text-tertiary-100"
              />
            </div>
          )}
          {/* Stream error */}
          {streamError && (
            <div className="flex h-full items-center justify-center">
              <Alert
                text={streamError}
                labelText="Warning"
                variant="warning"
                data-testid="stream-error-alert"
                textClassName="text-tertiary-100"
              />
            </div>
          )}

          <div className="flex flex-col gap-14 px-4 xl:flex-row xl:px-0">
            {/* AI response */}
            <div className="flex-1">
              <AiMessage
                messages={messages}
                isLoading={isSendingMessage || isStreaming || isLoadingMessages}
              />
            </div>
            {/* Sources- given there isn't an error, render */}
            {!isSourcesDataError && (
              <div className="mb-4 flex xl:w-64">
                {/* Sources component */}
                <Sources
                  sources={sourcesData}
                  isLoading={isLoadingSourcesData}
                />
              </div>
            )}
          </div>
        </div>
        <div
          className={twMerge(
            "relative bottom-0 left-0 z-10 mx-auto mb-6 grid w-full max-w-4xl content-end px-4",
            !isOpen &&
              !isMessageShort &&
              "pointer-events-none bg-linear-to-t from-secondary-900 from-2% to-transparent dark:from-secondary-950"
          )}
        >
          <div
            className={twMerge(
              "flex",
              !isOpen && !isMessageShort ? "justify-between" : "justify-end"
            )}
          >
            {hasMessages && !isOpen && !isMessageShort && (
              <Button
                id="read-more"
                data-testid="read-more"
                variant="primary"
                size="lg"
                onClick={handleToggleOpen}
                className="pointer-events-auto"
              >
                {t("OmniSearch.readMore")}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            )}
            <Typography className="pointer-events-auto text-right text-sm font-normal text-tertiary-300">
              {t("SearchResults.searchEngine", { engine: "PonGPT" })}
            </Typography>
          </div>
        </div>
      </div>
      {/* Storyblok Search Results */}
      {(messageFromUrl || persistedSearchTerm) && (
        <StoryblokSearchResults
          searchTerm={persistedSearchTerm || messageFromUrl || ""}
        />
      )}
    </Layout>
  );
};
